import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuthContext } from '../../contexts/AuthContext';
import AuthLayout, { CornerLogo, AUTH } from './AuthLayout';

const COUNTDOWN_SECONDS = 3;

export default function SignatureVerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as { from?: string; returnTo?: string } | null;
  const fromSettings = navState?.from === 'settings';
  const returnTo = navState?.returnTo && navState.returnTo.startsWith('/') ? navState.returnTo : null;
  const { getToken, refreshUser, user } = useAuthContext();
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [pendingSignatureImage, setPendingSignatureImage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    const stored = sessionStorage.getItem('corner:pending-signature');
    if (!stored) {
      navigate('/auth/signature-setup');
      return;
    }
    setPendingSignatureImage(stored);
  }, [navigate]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const progressPercent = ((COUNTDOWN_SECONDS - countdown) / COUNTDOWN_SECONDS) * 100;
  const canConfirm = countdown <= 0;

  const handleConfirm = async () => {
    if (!pendingSignatureImage) return;
    const token = getToken();
    if (!token) {
      setSaveError('Your session has expired. Please sign in again.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      await axios.put(
        '/api/auth/me/signature',
        { signature: pendingSignatureImage },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await refreshUser();
      sessionStorage.removeItem('corner:pending-signature');
      navigate(fromSettings ? '/app/settings' : (returnTo ?? '/auth/welcome'));
    } catch {
      setSaveError('Failed to save signature. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRedo = () => {
    sessionStorage.removeItem('corner:pending-signature');
    const setupQs = returnTo && !fromSettings ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
    navigate(
      `/auth/signature-setup${setupQs}`,
      fromSettings ? { state: { from: 'settings' } } : undefined,
    );
  };

  return (
    <AuthLayout>
      {/* 520px card — wider than auth card, matching Signature Setup */}
      <div
        style={{
          width: '100%',
          maxWidth: 520,
          backgroundColor: AUTH.cardBg,
          borderRadius: 16,
          border: `1px solid ${AUTH.cardBorder}`,
          boxShadow: AUTH.cardShadow,
          overflow: 'hidden',
          fontFamily: '"Geist", sans-serif',
        }}
      >
        {/* Header — logo + countdown badge */}
        <div
          style={{
            padding: '24px 28px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CornerLogo size={24} />
              <span style={{ fontSize: 16, fontWeight: 700, color: AUTH.textPrimary, marginLeft: -4 }}>
                Verify your signature
              </span>
            </div>
            {countdown > 0 ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 12px',
                  borderRadius: 20,
                  border: '1px solid #FBDACC',
                  backgroundColor: '#FFF7F3',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={AUTH.accent} strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12,6 12,12 16,14" />
                </svg>
                <span style={{ fontSize: 13, fontWeight: 500, color: AUTH.accent }}>
                  Review in {countdown}s
                </span>
              </div>
            ) : (
              <span style={{ fontSize: 13, color: AUTH.textMuted, fontWeight: 400 }}>
                Step 1 of 1
              </span>
            )}
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              fontWeight: 400,
              color: AUTH.textMuted,
              lineHeight: '16px',
              paddingLeft: 30,
            }}
          >
            Take a moment to confirm this is exactly how you want to sign.
          </p>
        </div>

        {/* Divider */}
        <div style={{ height: 1, backgroundColor: '#F0EDE8' }} />

        {/* Content */}
        <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Legal warning banner */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '12px 14px',
              backgroundColor: 'var(--auth-accent-badge-bg)',
              borderRadius: 9,
              border: '1px solid var(--auth-border, #E8E0D8)',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
              <circle cx="12" cy="12" r="10" stroke="rgba(139,125,107,0.70)" strokeWidth="2" />
              <line x1="12" y1="16" x2="12" y2="12" stroke="rgba(139,125,107,0.70)" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="8" r="1" fill="rgba(139,125,107,0.70)" />
            </svg>
            <span style={{ fontSize: 12, color: 'var(--auth-text-secondary, #8B7D6B)', lineHeight: '16px' }}>
              Your e-signature carries the same legal weight as a handwritten signature. Make sure it looks right.
            </span>
          </div>

          {/* YOUR SIGNATURE label */}
          <p
            style={{
              margin: 0,
              fontSize: 12,
              fontWeight: 500,
              color: AUTH.textSecondary,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            YOUR SIGNATURE
          </p>

          {/* Signature preview */}
          <div
            style={{
              border: `1px solid ${AUTH.cardBorder}`,
              borderRadius: 10,
              padding: 24,
              backgroundColor: '#FDFCFA',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            {pendingSignatureImage && (
              <img
                src={pendingSignatureImage}
                alt="Your signature"
                style={{ maxWidth: '80%', maxHeight: 80, objectFit: 'contain' }}
              />
            )}
            {/* Baseline */}
            <div
              style={{
                width: '85%',
                height: 1,
                backgroundColor: '#E8E2DA',
                marginTop: 12,
              }}
            />
            <span
              style={{
                alignSelf: 'flex-end',
                marginTop: 6,
                fontSize: 12,
                color: AUTH.textMuted,
              }}
            >
              {user?.displayName || 'Your name'}
            </span>
          </div>

          {/* Progress bar */}
          <div>
            <div
              style={{
                width: '100%',
                height: 3,
                backgroundColor: '#F2EDE6',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${progressPercent}%`,
                  height: '100%',
                  backgroundColor: AUTH.accent,
                  transition: 'width 1s linear',
                  borderRadius: 2,
                }}
              />
            </div>
            {countdown > 0 && (
              <p
                style={{
                  margin: '8px 0 0',
                  fontSize: 13,
                  color: AUTH.textPlaceholder,
                  textAlign: 'center',
                }}
              >
                Confirm button activates in {countdown} seconds — take a good look
              </p>
            )}
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, backgroundColor: '#F0EDE8' }} />

        {/* Footer buttons */}
        <div
          style={{
            padding: '16px 28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <button
            onClick={handleRedo}
            style={{
              height: 38,
              padding: '0 16px',
              fontSize: 13,
              fontWeight: 500,
              backgroundColor: 'transparent',
              border: '1px solid #E4DDD4',
              borderRadius: 8,
              cursor: 'pointer',
              fontFamily: '"Geist", sans-serif',
              color: AUTH.textPrimary,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            &larr; Redo signature
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm || saving}
            style={{
              height: 38,
              padding: '0 20px',
              fontSize: 13,
              fontWeight: 600,
              backgroundColor: canConfirm && !saving ? AUTH.accent : '#E8C4B0',
              border: 'none',
              borderRadius: 8,
              cursor: canConfirm && !saving ? 'pointer' : 'not-allowed',
              fontFamily: '"Geist", sans-serif',
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {countdown > 0 && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12,6 12,12 16,14" />
              </svg>
            )}
            {saving ? 'Saving…' : 'Confirm signature \u2192'}
          </button>
        </div>

        {saveError && (
          <div style={{ padding: '0 28px 12px', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: 12, color: AUTH.textError }}>{saveError}</p>
          </div>
        )}
      </div>
    </AuthLayout>
  );
}
